import React, { useState, useEffect } from 'react';
import {
    Typography,
    Box,
    Button,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    CircularProgress,
    DialogActions,
    IconButton,
    TextField,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Tooltip
} from '@mui/material';
import { Close as CloseIcon, Edit as EditIcon, ZoomIn as ZoomInIcon, Delete as DeleteIcon, MoneyOff as MoneyOffIcon } from '@mui/icons-material';
import { doc, getDoc, updateDoc, collection, query, where, getDocs, limit, orderBy, arrayRemove } from 'firebase/firestore';
import { ref, getDownloadURL, listAll } from 'firebase/storage';
import { db, storage } from '../../firebase';
import { Event, EVENT_ZONES } from '../../types/Event';
import { useAuth } from '../../contexts/AuthContext';
import { useParticipants } from '../../hooks/useParticipants';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { EventStats } from '../../hooks/useAdminEventStats';
import { adminStyles } from '../../theme/theme';

interface EventDebriefDialogProps {
    eventId: string | null;
    open: boolean;
    onClose: () => void;
    adminStats?: EventStats;
    onEventUpdated?: () => void;
    onEdit?: (event: Event) => void;
    onDelete?: (eventId: string) => void;
    onLocationClick: (locationId: string) => void;
}

interface EventPhoto {
    url: string;
    date: string;
    path: string;
}

const EventDebriefDialog: React.FC<EventDebriefDialogProps> = ({ eventId, open, onClose, adminStats, onEventUpdated, onEdit, onDelete, onLocationClick }) => {
    const { user, isAdmin } = useAuth();
    const { participants, fetchParticipants } = useParticipants();

    const [event, setEvent] = useState<Event | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [openDebriefDialog, setOpenDebriefDialog] = useState(false);
    const [debriefContent, setDebriefContent] = useState('');
    const [eventPhotos, setEventPhotos] = useState<EventPhoto[]>([]);
    const [loadingPhotos, setLoadingPhotos] = useState(false);
    const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
    const [homeAddressDialogOpen, setHomeAddressDialogOpen] = useState(false);

    // ... useEffects ...

    const handleLocationClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!event) return;

        if (event.zone === EVENT_ZONES.WAN) {
            if (event.location) window.open(event.location.startsWith('http') ? event.location : `https://${event.location}`, '_blank');
        } else if (event.zone === EVENT_ZONES.COMMUNAL) {
            if (event.locationId) {
                onLocationClick(event.locationId);
            } else if (event.googleMapsUrl) {
                window.open(event.googleMapsUrl, '_blank');
            } else if (event.location) {
                window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}`, '_blank');
            }
        } else if (event.zone === EVENT_ZONES.HOME) {
            setHomeAddressDialogOpen(true);
        }
    };

    useEffect(() => {
        if (event && event.participants && isAdmin) {
            fetchParticipants(event.id || '', event.participants);
        }
    }, [event, isAdmin]);

    useEffect(() => {
        if (!open || !eventId) {
            setEvent(null);
            return;
        }

        const fetchEvent = async () => {
            setLoading(true);
            try {
                const eventDoc = await getDoc(doc(db, 'events', eventId));
                if (eventDoc.exists()) {
                    const eventData = { id: eventDoc.id, ...eventDoc.data() } as Event;
                    setEvent(eventData);
                    setDebriefContent(eventDoc.data().debrief?.content || '');

                    fetchEventPhotos(eventData);
                } else {
                    setError('Événement non trouvé');
                }
            } catch (err) {
                setError('Erreur lors du chargement de l\'événement');
            } finally {
                setLoading(false);
            }
        };

        fetchEvent();
    }, [eventId, open]);

    const handleRemoveParticipant = async (participantId: string) => {
        if (!event || !event.id) return;
        if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce participant de l'événement ?")) return;

        try {
            const eventRef = doc(db, 'events', event.id);
            await updateDoc(eventRef, {
                participants: arrayRemove(participantId)
            });

            // Update local state
            const updatedParticipants = event.participants.filter(id => id !== participantId);
            setEvent({ ...event, participants: updatedParticipants });

            // Trigger parent update
            if (onEventUpdated) onEventUpdated();

            // Re-fetch participants to update the list locally immediately
            fetchParticipants(event.id, updatedParticipants);

        } catch (err) {
            console.error(err);
            alert("Erreur lors de la suppression du participant");
        }
    };

    // Fonction pour obtenir l'URL Google Maps
    const getGoogleMapsUrl = (location: string) => {
        return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`;
    };

    // Logic from EventDebrief.tsx
    const fetchEventPhotos = async (eventData: Event) => {
        if (!eventData.startDate || !eventData.endDate) return;

        setLoadingPhotos(true);

        const eventStart = new Date(eventData.startDate);
        const eventEnd = new Date(eventData.endDate);

        eventStart.setHours(eventStart.getHours() - 2);
        eventEnd.setHours(eventEnd.getHours() + 2);

        try {
            if (user) {
                try {
                    const photosRef = collection(db, 'photos');
                    const q = query(
                        photosRef,
                        where('date', '>=', eventStart),
                        where('date', '<=', eventEnd),
                        orderBy('date', 'desc'),
                        limit(100)
                    );

                    const querySnapshot = await getDocs(q);

                    const photoPromises = querySnapshot.docs.map(async (doc) => {
                        const photoData = doc.data();
                        if (photoData.storagePath) {
                            try {
                                const storageRef = ref(storage, photoData.storagePath);
                                const url = await getDownloadURL(storageRef);
                                return {
                                    url,
                                    date: photoData.date,
                                    path: photoData.storagePath
                                };
                            } catch (err) {
                                return null;
                            }
                        }
                        return null;
                    });

                    const photoResults = await Promise.all(photoPromises);
                    const validPhotos = photoResults.filter(p => p !== null) as EventPhoto[];

                    if (validPhotos.length > 0) {
                        setEventPhotos(validPhotos);
                        setLoadingPhotos(false);
                        return;
                    }

                    // Try path prefix search
                    const eventYear = eventStart.getFullYear();
                    const eventMonth = (eventStart.getMonth() + 1).toString().padStart(2, '0');
                    const pathPrefix = `photos/${eventYear}/${eventMonth}/`;

                    const pathQuery = query(
                        photosRef,
                        where('path', '>=', pathPrefix),
                        where('path', '<', pathPrefix + '\uf8ff'),
                        limit(200)
                    );

                    const pathSnapshot = await getDocs(pathQuery);
                    const pathPhotoPromises = pathSnapshot.docs.map(async (doc) => {
                        const photoData = doc.data();
                        if (photoData.storagePath || photoData.path) {
                            try {
                                const storagePath = photoData.storagePath || photoData.path;
                                const storageRef = ref(storage, storagePath);
                                const url = await getDownloadURL(storageRef);

                                let photoDate = photoData.date;
                                if (photoDate && typeof photoDate.toDate === 'function') {
                                    photoDate = photoData.date.toDate();
                                }

                                if (!photoDate || (photoDate >= eventStart && photoDate <= eventEnd)) {
                                    return {
                                        url,
                                        date: photoData.date ? photoData.date.toString() : '',
                                        path: storagePath
                                    };
                                }
                            } catch (err) {
                                return null;
                            }
                        }
                        return null;
                    });

                    const pathResults = await Promise.all(pathPhotoPromises);
                    const validPathPhotos = pathResults.filter(p => p !== null) as EventPhoto[];

                    if (validPathPhotos.length > 0) {
                        setEventPhotos(validPathPhotos);
                        setLoadingPhotos(false);
                        return;
                    }

                } catch (err) {
                    console.error(err);
                }
            }

            await fetchPhotosFromStorage(eventStart, eventEnd);

        } catch (err) {
            console.error(err);
        } finally {
            setLoadingPhotos(false);
        }
    };

    const fetchPhotosFromStorage = async (startDate: Date, endDate: Date) => {
        try {
            const startYear = startDate.getFullYear();
            const startMonth = startDate.getMonth() + 1;
            const endYear = endDate.getFullYear();
            const endMonth = endDate.getMonth() + 1;

            const foldersToCheck: string[] = [];

            if (startYear === endYear && startMonth === endMonth) {
                foldersToCheck.push(`photos/${startYear}/${startMonth.toString().padStart(2, '0')}`);
            } else {
                let currentYear = startYear;
                let currentMonth = startMonth;

                while (currentYear < endYear || (currentYear === endYear && currentMonth <= endMonth)) {
                    foldersToCheck.push(`photos/${currentYear}/${currentMonth.toString().padStart(2, '0')}`);

                    currentMonth++;
                    if (currentMonth > 12) {
                        currentMonth = 1;
                        currentYear++;
                    }
                }
            }

            const allPhotos: EventPhoto[] = [];

            for (const folder of foldersToCheck) {
                try {
                    const folderRef = ref(storage, folder);
                    const filesList = await listAll(folderRef);

                    for (const fileRef of filesList.items) {
                        try {
                            const url = await getDownloadURL(fileRef);
                            const fileName = fileRef.name;

                            let photoDate: Date | null = null;
                            const dateMatch = fileName.match(/(\d{8})_(\d{6})/);

                            if (dateMatch) {
                                const dateStr = dateMatch[1];
                                const timeStr = dateMatch[2];
                                const year = parseInt(dateStr.substring(0, 4));
                                const month = parseInt(dateStr.substring(4, 6)) - 1;
                                const day = parseInt(dateStr.substring(6, 8));
                                const hour = parseInt(timeStr.substring(0, 2));
                                const minute = parseInt(timeStr.substring(2, 4));
                                const second = parseInt(timeStr.substring(4, 6));

                                photoDate = new Date(year, month, day, hour, minute, second);
                            } else {
                                const folderParts = folder.split('/');
                                if (folderParts.length >= 3) {
                                    const year = parseInt(folderParts[1]);
                                    const month = parseInt(folderParts[2]) - 1;
                                    photoDate = new Date(year, month, 15);
                                }
                            }

                            if (photoDate && photoDate >= startDate && photoDate <= endDate) {
                                allPhotos.push({
                                    url,
                                    date: photoDate.toISOString(),
                                    path: fileRef.fullPath
                                });
                            }
                        } catch (err) {
                            // Ignore errors for individual files
                        }
                    }
                } catch (err) {
                    // Ignore folder errors
                }
            }

            allPhotos.sort((a, b) => a.url.localeCompare(b.url));
            setEventPhotos(allPhotos);

        } catch (err) {
            console.error(err);
        }
    };

    const handleSaveDebrief = async () => {
        try {
            if (!eventId || !user) return;

            await updateDoc(doc(db, 'events', eventId), {
                debrief: {
                    content: debriefContent,
                    lastModified: new Date().toISOString(),
                    modifiedBy: user.uid
                }
            });

            setEvent(prev => prev ? {
                ...prev,
                debrief: {
                    content: debriefContent,
                    lastModified: new Date().toISOString(),
                    modifiedBy: user.uid
                }
            } : null);

            setOpenDebriefDialog(false);
        } catch (err) {
            setError('Erreur lors de la sauvegarde du débriefing');
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            scroll="body"
        >
            <Box sx={{ position: 'relative', bgcolor: 'background.default', minHeight: '50vh', p: 1 }}>
                <IconButton
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                        zIndex: 10
                    }}
                >
                    <CloseIcon />
                </IconButton>

                <DialogContent>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                            <CircularProgress />
                        </Box>
                    ) : event ? (
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h4" component="h1" sx={{ mr: 2, mb: 0 }}>
                                    {event.title}
                                </Typography>
                                {isAdmin && (
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Tooltip title="Modifier">
                                            <IconButton
                                                onClick={() => onEdit && onEdit(event)}
                                                size="small"
                                                sx={{
                                                    ...adminStyles.glassButton,
                                                    '&:hover': {
                                                        ...adminStyles.glassButton['&:hover'],
                                                        bgcolor: 'primary.main'
                                                    }
                                                }}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Supprimer">
                                            <IconButton
                                                onClick={() => {
                                                    if (onDelete && event.id) {
                                                        onDelete(event.id);
                                                        onClose(); // Close dialog after delete
                                                    }
                                                }}
                                                size="small"
                                                sx={{
                                                    ...adminStyles.glassButton,
                                                    '&:hover': {
                                                        ...adminStyles.glassButton['&:hover'],
                                                        bgcolor: 'error.main'
                                                    }
                                                }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                )}
                            </Box>

                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="h6" gutterBottom>
                                        Informations
                                    </Typography>
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="body1" gutterBottom>
                                            <strong>Date :</strong> {format(new Date(event.startDate), 'dd MMMM yyyy HH:mm', { locale: fr })}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                            <Typography variant="body1" component="span" sx={{ fontWeight: 'bold', mr: 0.5 }}>
                                                Lieu :
                                            </Typography>
                                            <Typography
                                                variant="body1"
                                                component="span"
                                                onClick={handleLocationClick}
                                                sx={{
                                                    cursor: 'pointer',
                                                    color: 'primary.main',
                                                    textDecoration: 'underline',
                                                    textDecorationStyle: 'dotted',
                                                    textUnderlineOffset: '4px',
                                                    '&:hover': { opacity: 0.8 }
                                                }}
                                            >
                                                {event.zone === EVENT_ZONES.WAN ? 'Discord (Rejoindre)' : (event.location || 'Voir sur la carte')}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {isAdmin && adminStats && (
                                        <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(255, 215, 0, 0.1)', borderRadius: 1, border: '1px solid rgba(255, 215, 0, 0.3)' }}>
                                            <Typography variant="subtitle2" sx={{ color: '#FFD700', fontWeight: 'bold', mb: 1 }}>
                                                Statistiques Admin
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Participants :</strong> {adminStats.totalParticipants} / {adminStats.capacity}
                                            </Typography>
                                            {/* Paiements summary removed as requested */}

                                            {/* Liste des participants avec suppression */}
                                            {participants.length > 0 && (
                                                <Box sx={{ mt: 2 }}>
                                                    <List dense disablePadding sx={{ mt: 1, maxHeight: 200, overflowY: 'auto', bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 1 }}>
                                                        {participants.map((p) => {
                                                            const isUnpaid = adminStats.unpaidParticipantIds.includes(p.id);
                                                            const displayName = `${p.firstName || ''} ${p.lastName || ''} ${p.nickname ? `(${p.nickname})` : ''}`.trim() || p.name;

                                                            return (
                                                                <ListItem key={p.id} divider>
                                                                    <ListItemText
                                                                        primary={
                                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                                {isUnpaid && (
                                                                                    <Tooltip title="N'a pas payé">
                                                                                        <MoneyOffIcon color="error" fontSize="small" sx={{ mr: 1, fontSize: 16 }} />
                                                                                    </Tooltip>
                                                                                )}
                                                                                <Typography variant="body2" color={isUnpaid ? "error.light" : "text.primary"}>
                                                                                    {displayName}
                                                                                </Typography>
                                                                            </Box>
                                                                        }
                                                                    />
                                                                    <ListItemSecondaryAction>
                                                                        <IconButton
                                                                            edge="end"
                                                                            aria-label="delete"
                                                                            size="small"
                                                                            onClick={() => handleRemoveParticipant(p.id)}
                                                                            sx={{ color: 'error.main', opacity: 0.7, '&:hover': { opacity: 1 } }}
                                                                        >
                                                                            <DeleteIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </ListItemSecondaryAction>
                                                                </ListItem>
                                                            );
                                                        })}
                                                    </List>
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography variant="h6" gutterBottom>
                                        Photos de l'événement
                                    </Typography>

                                    {user && event.photoAlbumUrl && (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            href={event.photoAlbumUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{ mb: 2, mr: 2 }}
                                        >
                                            Voir l'album photo complet
                                        </Button>
                                    )}

                                    <Box sx={{ mt: 2 }}>
                                        {!user ? (
                                            <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
                                                Les photos ne sont visibles que par les membres.
                                            </Typography>
                                        ) : loadingPhotos ? (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                                                <CircularProgress />
                                            </Box>
                                        ) : eventPhotos.length > 0 ? (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
                                                {eventPhotos.map((photo, index) => (
                                                    <Box
                                                        key={index}
                                                        sx={{
                                                            width: 150,
                                                            height: 150,
                                                            position: 'relative',
                                                            cursor: 'pointer',
                                                            borderRadius: 1,
                                                            overflow: 'hidden',
                                                            zIndex: 1,
                                                            transition: 'transform 0.3s, z-index 0s',
                                                            '&:hover': {
                                                                transform: 'scale(1.5)',
                                                                zIndex: 10,
                                                                boxShadow: '0 5px 15px rgba(0, 0, 0, 0.3)',
                                                                '& .zoom-icon': { opacity: 1 },
                                                                '& img': { objectFit: 'contain' }
                                                            }
                                                        }}
                                                        onClick={() => setSelectedPhoto(photo.url)}
                                                    >
                                                        <img
                                                            src={photo.url}
                                                            alt={`Photo ${index + 1}`}
                                                            loading="lazy"
                                                            style={{
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'cover',
                                                                transition: 'object-fit 0.3s'
                                                            }}
                                                        />
                                                        <Box
                                                            className="zoom-icon"
                                                            sx={{
                                                                position: 'absolute',
                                                                bottom: '8px',
                                                                right: '8px',
                                                                backgroundColor: 'rgba(0,0,0,0.5)',
                                                                borderRadius: '50%',
                                                                p: 0.5,
                                                                opacity: 0,
                                                                transition: 'opacity 0.2s'
                                                            }}
                                                        >
                                                            <ZoomInIcon sx={{ color: 'white', fontSize: 20 }} />
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </Box>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                Aucune photo trouvée.
                                            </Typography>
                                        )}
                                    </Box>
                                </Grid>

                                {(event.debrief?.content || isAdmin) && (
                                    <Grid item xs={12}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                            <Typography variant="h6">
                                                Débriefing
                                            </Typography>
                                            {isAdmin && (
                                                <Button
                                                    startIcon={<EditIcon />}
                                                    onClick={() => setOpenDebriefDialog(true)}
                                                >
                                                    {event.debrief ? 'Modifier' : 'Ajouter'}
                                                </Button>
                                            )}
                                        </Box>
                                        {event.debrief && (
                                            <>
                                                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
                                                    {event.debrief.content}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Dernière modification le {format(new Date(event.debrief.lastModified), 'dd/MM/yyyy HH:mm')}
                                                </Typography>
                                            </>
                                        )}
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    ) : (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography color="error">{error || 'Compte rendu introuvable'}</Typography>
                        </Box>
                    )
                    }
                </DialogContent>
            </Box>

            {/* Nested Dialogs */}
            <Dialog
                open={openDebriefDialog}
                onClose={() => setOpenDebriefDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Modifier le débriefing</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        multiline
                        rows={10}
                        value={debriefContent}
                        onChange={(e) => setDebriefContent(e.target.value)}
                        fullWidth
                        variant="outlined"
                        placeholder="Écrivez ici le débriefing..."
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDebriefDialog(false)}>
                        Annuler
                    </Button>
                    <Button onClick={handleSaveDebrief} variant="contained">
                        Enregistrer
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={!!selectedPhoto}
                onClose={() => setSelectedPhoto(null)}
                maxWidth="lg"
                fullWidth
            >
                <DialogContent sx={{ p: 0, overflow: 'hidden', textAlign: 'center', bgcolor: '#000', position: 'relative' }}>
                    <IconButton onClick={() => setSelectedPhoto(null)} sx={{ position: 'absolute', right: 8, top: 8, color: 'white' }}>
                        <CloseIcon />
                    </IconButton>
                    {selectedPhoto && (
                        <img
                            src={selectedPhoto}
                            alt="Extended view"
                            style={{
                                maxWidth: '100%',
                                maxHeight: '80vh',
                                objectFit: 'contain'
                            }}
                        />
                    )}
                </DialogContent>
            </Dialog>

            {/* Dialog Adresse Domicile */}
            <Dialog
                open={homeAddressDialogOpen}
                onClose={() => setHomeAddressDialogOpen(false)}
                onClick={(e) => e.stopPropagation()}
            >
                <DialogTitle>Adresse de l'événement</DialogTitle>
                <DialogContent>
                    <Typography>
                        Cet événement a lieu au domicile d'un membre.
                        Pour obtenir l'adresse précise, veuillez contacter directement un administrateur ou l'organisateur.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setHomeAddressDialogOpen(false)}>Fermer</Button>
                </DialogActions>
            </Dialog>

        </Dialog>
    );
};

export default EventDebriefDialog;
